import { ReactElement, useMemo, useState } from 'react'
import { useQuery } from 'urql'
import { graphql } from 'app/generated/gql'
import Combo from 'app/components/ui/combo/combo'
import { DraggableContact } from '../contacts/DraggableContact'
import { closeModal } from 'app/lib/modal'
import { DEFAULT_LIST_CARD_CONFIG, META_MAX, ListCardConfig } from 'lib/listCardConfig'
import { fieldsForSlot } from 'lib/listCardFields'
import type { CardSlotName } from 'lib/listCardConfig'
import type { TaskCounts } from 'app/hooks/usePipelineTaskCounts'
import type { CommContext } from 'app/hooks/usePipelineCommContext'
import type { ContactJson } from 'app/types/entities'
import type { OptionType } from 'types/OptionType'

// Grab one location from the current workspace so the preview's Location field shows a
// real name instead of "-". Auth-scoped, so it returns a location the user can see.
const PREVIEW_LOCATION_QUERY = graphql(`
  query PipelineCardPreviewLocation($pagination: PaginationInput) {
    locations(pagination: $pagination) {
      hits {
        id
        name
      }
    }
  }
`)

const DAY = 86400000

// A richly-populated sample so that whichever field a user drops into a slot, the
// preview shows realistic data instead of "-". Cast because it only needs the subset
// of fields the card field accessors read.
const SAMPLE: ContactJson = {
  id: `sample`,
  name: `Jane Cooper`,
  birthDate: `1984-03-12`,
  email: `jane.cooper@example.com`,
  cellPhone: `(415) 555-0132`,
  homePhone: `(415) 555-0177`,
  workPhone: `(415) 555-0188`,
  lifecycleStage: `Patient`,
  patientStatus: `Patient`,
  leadStatus: `Closed Won`,
  leadOwner: { name: `Dr. Alex Kim` },
  isQualified: true,
  channel: `Phone`,
  utmSource: `google`,
  utmMedium: `cpc`,
  utmCampaign: `spring-whitening`,
  utmContent: `hero-cta`,
  appointmentStatus: `Scheduled`,
  addressLine1: `2118 Thornridge Cir`,
  addressLine2: `Suite 200`,
  city: `Syracuse`,
  zip: `13202`,
  preferredCommunicationMethod: `Email`,
  preferredContact: `Cell`,
  collectedLtv: 3450,
  productionLtv: 5200,
  scheduledProduction: 12400,
  lastCommDate: new Date(Date.now() - 4 * DAY).toISOString(),
  firstActivityDate: `2025-11-02`,
  lastActivityDate: `2026-06-30`,
  becameLeadDate: `2025-11-02`,
  becamePatientDate: `2026-01-15`,
  firstAppointmentDate: `2026-01-15`,
  lastAppointmentDate: `2026-05-20`,
  nearestAppointmentDate: new Date(Date.now() + 14 * DAY).toISOString(),
  lastHygieneDate: `2026-05-20`,
  isPerio: false,
  leadConversionStatus: `Converted`,
  firstContactReason: `New patient inquiry`,
  firstActivityType: `Call`,
  reportedAttribution: `Google Search`,
  optIn: true,
  smsOptIn: true,
  emailOptIn: true,
} as unknown as ContactJson

// A cold, thinly-populated contact: shows how the card reads when money is $0, the
// contact has gone quiet, and several fields are empty.
const SAMPLE_COLD: ContactJson = {
  id: `sample-cold`,
  name: `Marcus Lee`,
  birthDate: `1971-08-04`,
  email: `m.lee@example.com`,
  cellPhone: `(212) 555-0114`,
  leadStatus: `In Progress`,
  leadOwner: { name: `Sam Rivera` },
  isQualified: false,
  channel: `Web`,
  utmSource: `facebook`,
  utmMedium: `social`,
  patientStatus: `Lead`,
  appointmentStatus: `None`,
  city: `Rochester`,
  zip: `14604`,
  collectedLtv: 0,
  productionLtv: 0,
  scheduledProduction: 0,
  lastCommDate: new Date(Date.now() - 45 * DAY).toISOString(),
  lastActivityDate: `2026-05-01`,
} as unknown as ContactJson

// A brand-new lead with minimal data.
const SAMPLE_NEW: ContactJson = {
  id: `sample-new`,
  name: `Ava Brooks`,
  leadStatus: `New`,
  leadOwner: { name: `Unassigned` },
  patientStatus: `Lead`,
  channel: `Phone`,
  city: `Ithaca`,
  scheduledProduction: 4200,
  lastCommDate: new Date().toISOString(),
} as unknown as ContactJson

const SAMPLES: { contact: ContactJson; taskCounts: TaskCounts; commContext: CommContext }[] = [
  {
    contact: SAMPLE,
    taskCounts: { completed: 2, open: 3, overdue: 1 },
    commContext: {
      lastComm: { direction: `inbound`, type: `text`, date: new Date(Date.now() - 4 * DAY).toISOString() },
      hasUnread: true,
    },
  },
  {
    contact: SAMPLE_COLD,
    taskCounts: { completed: 5, open: 0, overdue: 2 },
    commContext: {
      lastComm: { direction: `outbound`, type: `email`, date: new Date(Date.now() - 45 * DAY).toISOString() },
      hasUnread: false,
    },
  },
  {
    contact: SAMPLE_NEW,
    taskCounts: { completed: 0, open: 1, overdue: 0 },
    commContext: {
      lastComm: { direction: `inbound`, type: `text`, date: new Date().toISOString() },
      hasUnread: true,
    },
  },
]

/**
 * De-dupe a Details selection and cap it at `max` field ids. Pure — unit tested.
 */
export function capMetaSelection(ids: string[], max: number = META_MAX): string[] {
  return [...new Set(ids)].slice(0, max)
}

const NONE_OPTION: OptionType = { id: `__none__`, value: ``, name: `None` }
const COMBO_INPUT = `!h-9 !rounded-md !px-3 !py-0 !shadow-none !text-body-sm`

function slotOptions(slot: CardSlotName, exclude?: string | null): OptionType[] {
  const fields = fieldsForSlot(slot)
    .filter((f) => f.id !== exclude)
    .map((f) => ({ id: f.id, value: f.id, name: f.label }))
  return [NONE_OPTION, ...fields]
}

function SlotCombo({
  label,
  slot,
  value,
  exclude,
  onChange,
}: {
  label: string
  slot: CardSlotName
  value: string | null
  exclude?: string | null
  onChange: (v: string | null) => void
}): ReactElement {
  const options = slotOptions(slot, exclude)
  const selected = options.find((o) => o.value === (value ?? ``)) ?? NONE_OPTION
  return (
    <div className="flex flex-col gap-y-1">
      <span className="text-ui-xs text-default">{label}</span>
      <Combo
        options={options}
        value={selected}
        onChange={(opt) => onChange((opt?.value as string) || null)}
        showSearchInDropdown
        useSingleFieldInput={false}
        placeholder="None"
        inputClassName={COMBO_INPUT}
        chevronClassName="!h-4 !w-4 !text-minimal"
        optionsContainerClassName="py-1"
      />
    </div>
  )
}

function MetaSelect({
  selectedIds,
  onChange,
}: {
  selectedIds: string[]
  onChange: (ids: string[]) => void
}): ReactElement {
  const atCap = selectedIds.length >= META_MAX
  const options: OptionType[] = fieldsForSlot(`meta`).map((f) => ({
    id: f.id,
    value: f.id,
    name: f.label,
    // Disable unselected options once the cap is reached, so the list can't exceed the max.
    meta: !selectedIds.includes(f.id) && atCap ? { isDisabled: true } : undefined,
  }))
  const selected = options.filter((o) => selectedIds.includes(o.value as string))
  return (
    <div className="flex flex-col gap-y-1">
      <span className="text-ui-xs text-default">Additional details (up to {META_MAX})</span>
      <Combo
        options={options}
        selected={selected}
        multiple
        onChangeMultiple={(vals) => onChange(capMetaSelection(vals.map((v) => v.value as string)))}
        showSearchInDropdown
        placeholder="None"
        inputClassName={COMBO_INPUT}
        chevronClassName="!h-4 !w-4 !text-minimal"
        optionsContainerClassName="py-1"
      />
    </div>
  )
}

function DisplayToggle({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
}): ReactElement {
  return (
    <label className="flex cursor-pointer items-center justify-between text-body-sm text-default">
      <span>{label}</span>
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-strong text-default focus:ring-0 focus:ring-offset-0"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
    </label>
  )
}

export function PipelineCardCustomization({
  initialConfig,
  onSave,
  previewCard,
}: {
  initialConfig: ListCardConfig
  onSave: (config: ListCardConfig) => void
  /** Absent for the legacy pipeline page, which still previews with `DraggableContact`. */
  previewCard?: (sample: {
    contact: ContactJson
    taskCounts: TaskCounts
    commContext: CommContext
    config: ListCardConfig
  }) => ReactElement
}): ReactElement {
  const [config, setConfig] = useState<ListCardConfig>(initialConfig)
  const [locationResult] = useQuery({ query: PREVIEW_LOCATION_QUERY, variables: { pagination: { limit: 1 } } })
  const previewLocationId: string | undefined = locationResult.data?.locations?.hits?.[0]?.id
  const samples = useMemo(
    () =>
      SAMPLES.map(({ contact, taskCounts, commContext }) => ({
        taskCounts,
        commContext,
        contact: previewLocationId ? ({ ...contact, locationId: previewLocationId } as ContactJson) : contact,
      })),
    [previewLocationId],
  )
  const setSlot = (slot: `subtitle` | `hero`, v: string | null) =>
    setConfig((c) => ({ ...c, slots: { ...c.slots, [slot]: v } }))
  const setMeta = (ids: string[]) => setConfig((c) => ({ ...c, slots: { ...c.slots, meta: capMetaSelection(ids) } }))

  return (
    <div className="flex h-[min(620px,80vh)] w-[720px] flex-col">
      <div className="flex min-h-0 flex-1">
        <div className="w-1/2 space-y-5 border-r border-default p-4">
          <div className="space-y-2">
            <span className="text-ui-xs font-medium uppercase tracking-wide text-subtle">Fields</span>
            <div className="space-y-3">
              <SlotCombo
                label="Subtitle"
                slot="subtitle"
                value={config.slots.subtitle}
                onChange={(v) => setSlot(`subtitle`, v)}
              />
              <SlotCombo label="Highlight" slot="hero" value={config.slots.hero} onChange={(v) => setSlot(`hero`, v)} />
              <MetaSelect selectedIds={config.slots.meta} onChange={setMeta} />
            </div>
          </div>
          <div className="space-y-2 border-t border-default pt-4">
            <span className="text-ui-xs font-medium uppercase tracking-wide text-subtle">Display</span>
            <div className="flex flex-col gap-y-2.5">
              <DisplayToggle
                label="Avatar"
                checked={config.showAvatar}
                onChange={(v) => setConfig((c) => ({ ...c, showAvatar: v }))}
              />
              <DisplayToggle
                label="Age"
                checked={config.showAge}
                onChange={(v) => setConfig((c) => ({ ...c, showAge: v }))}
              />
              <DisplayToggle
                label="Communications"
                checked={config.showCommunications}
                onChange={(v) => setConfig((c) => ({ ...c, showCommunications: v }))}
              />
            </div>
          </div>
          <div className="space-y-2 border-t border-default pt-4">
            <span className="text-ui-xs font-medium uppercase tracking-wide text-subtle">Density</span>
            <div className="flex flex-col gap-y-2.5">
              <DisplayToggle
                label="Compact"
                checked={config.density === `compact`}
                onChange={(v) => setConfig((c) => ({ ...c, density: v ? `compact` : `comfortable` }))}
              />
            </div>
          </div>
          <button
            className="text-ui-xs text-subtle underline-offset-2 hover:underline"
            onClick={() => setConfig(DEFAULT_LIST_CARD_CONFIG)}
          >
            Reset to defaults
          </button>
        </div>
        <div className="flex w-1/2 flex-col gap-y-3 overflow-y-auto bg-surface-subtle p-6" aria-label="Card preview">
          <span className="text-ui-xs font-medium uppercase tracking-wide text-subtle">Preview</span>
          {/* Visual-only preview across varied contacts: block drag / link / remove from the real cards */}
          <div className="pointer-events-none flex select-none flex-col items-center gap-y-3">
            {samples.map(({ contact, taskCounts, commContext }) => (
              <div key={contact.id!} className="w-72">
                {previewCard ? (
                  previewCard({ contact, taskCounts, commContext, config })
                ) : (
                  <DraggableContact
                    contact={contact}
                    config={config}
                    openContactDrawer={() => {}}
                    removeContact={() => {}}
                    taskCounts={taskCounts}
                    commContext={commContext}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 justify-end border-t border-default p-4">
        <button className="btn btn-secondary btn-md" onClick={closeModal}>
          Cancel
        </button>
        <button
          className="btn btn-primary btn-md ml-4"
          onClick={() => {
            onSave(config)
            closeModal()
          }}
        >
          Save
        </button>
      </div>
    </div>
  )
}
